import React, { useState, useEffect } from 'react';
import { getBestMonthlySales } from '../../api/salesApi';

const API_BASE = process.env.REACT_APP_API_BASE_URL?.replace(/\/api$/, '') || '';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Rank accent colours (1-3 medal, rest slate) — mirrors the Top Agents chart palette.
const RANK = {
    1: { bar: 'linear-gradient(90deg, #F59E0B, #D97706)', chip: '#F59E0B' },
    2: { bar: 'linear-gradient(90deg, #94A3B8, #64748B)', chip: '#94A3B8' },
    3: { bar: 'linear-gradient(90deg, #CD7F32, #A0522D)', chip: '#CD7F32' },
};
const restBar = 'linear-gradient(90deg, #3B82F6, #1A8CFF)';

const formatRM = (val) => {
    if (val >= 1_000_000) return `RM ${(val / 1_000_000).toFixed(2).replace(/\.?0+$/, '')}mil`;
    if (val >= 1000) return `RM ${(val / 1000).toFixed(0)}K`;
    return `RM ${Number(val || 0).toFixed(0)}`;
};

const initials = (a) => {
    if (a.firstName && a.lastName) return (a.lastName[0] + a.firstName[0]).toUpperCase();
    if (a.firstName) return a.firstName.slice(0, 2).toUpperCase();
    if (a.alias) return a.alias.slice(0, 2).toUpperCase();
    return '??';
};
const agentName = (a) => {
    const f = a.firstName?.trim(), l = a.lastName?.trim();
    if (f && l) return `${a.lastName} ${a.firstName}`;
    return f || l || a.alias || '';
};

export default function BestMonthlyLeaderboard() {
    const [rows, setRows] = useState([]);
    const [grown, setGrown] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const res = await getBestMonthlySales();
                setRows(res.data || []);
            } catch (err) { console.error(err); }
        })();
    }, []);

    useEffect(() => {
        setGrown(false);
        const t = setTimeout(() => setGrown(true), 80);
        return () => clearTimeout(t);
    }, [rows]);

    const max = rows.length ? Math.max(...rows.map(r => r.totalRevenue)) : 0;

    return (
        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <style>{`@keyframes bmlFlow { 0%,100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }`}</style>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <span style={{ fontSize: 16 }}>🏆</span>
                <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>Best All-Time Monthly Sales</h3>
                <span style={{ fontSize: 11, color: '#94A3B8', fontWeight: 500 }}>· record month per agent</span>
            </div>

            {!rows.length ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 120, color: '#94A3B8' }}>
                    No sales data yet
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {rows.map((a, i) => {
                        const rank = i + 1;
                        const accent = RANK[rank] || { bar: restBar, chip: '#94A3B8' };
                        const pct = max > 0 ? Math.max(6, (a.totalRevenue / max) * 100) : 6;
                        return (
                            <div key={`${a.userId}-${a.year}-${a.month}`} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                {/* rank */}
                                <div style={{ width: 22, textAlign: 'center', fontSize: 13, fontWeight: 800, color: accent.chip, flexShrink: 0 }}>
                                    {rank}
                                </div>
                                {/* avatar */}
                                {a.profilePhotoUrl ? (
                                    <img src={`${API_BASE}${a.profilePhotoUrl}`} alt={agentName(a)}
                                        style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${accent.chip}`, flexShrink: 0 }} />
                                ) : (
                                    <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10, fontWeight: 700, border: `2px solid ${accent.chip}`, flexShrink: 0 }}>
                                        {initials(a)}
                                    </div>
                                )}
                                {/* name + bar */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3 }}>
                                        <span style={{ fontSize: 12, fontWeight: 600, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {agentName(a)}
                                            <span style={{ fontSize: 10, color: '#94A3B8', fontWeight: 500, marginLeft: 6 }}>
                                                {MONTHS[(a.month || 1) - 1]} {a.year}
                                            </span>
                                        </span>
                                        <span style={{ fontSize: 12, fontWeight: 800, color: accent.chip, flexShrink: 0, marginLeft: 8 }}>
                                            {formatRM(a.totalRevenue)}
                                        </span>
                                    </div>
                                    <div style={{ height: 8, background: '#F1F5F9', borderRadius: 6, overflow: 'hidden' }}>
                                        <div style={{
                                            height: '100%', borderRadius: 6,
                                            width: grown ? `${pct}%` : '0%',
                                            background: accent.bar, backgroundSize: '200% 100%',
                                            transition: 'width 1s cubic-bezier(0.22,1,0.36,1)',
                                            animation: 'bmlFlow 4s ease-in-out infinite',
                                        }} />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
